import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { driver, Driver, DriveStep } from 'driver.js';
import 'driver.js/dist/driver.css';
import { getTourSteps, TourStep, TOUR_TOTAL_STEPS } from './tourSteps';
import './tourStyles.css';

// ─── Constants ───────────────────────────────────────────────────────
const STORAGE_KEY_PROGRESS = 'workflow-tour-progress';
const STORAGE_KEY_SEEN = 'workflow-tour-seen';

interface TourProgress {
  stepIndex: number;
  completed: boolean;
}

// ─── Context ─────────────────────────────────────────────────────────
interface TourContextValue {
  startTour: (fromBeginning?: boolean) => void;
  isActive: boolean;
  currentStep: number;
  totalSteps: number;
  hasSeenTour: boolean;
}

const TourContext = createContext<TourContextValue>({
  startTour: () => {},
  isActive: false,
  currentStep: 0,
  totalSteps: TOUR_TOTAL_STEPS,
  hasSeenTour: false,
});

export const useTour = () => useContext(TourContext);

// ─── Floating Action Card ────────────────────────────────────────────
// Rendered for isAction steps — a simple React div in bottom-left corner.
// Completely independent of driver.js — no overlay, no z-index conflicts.
interface ActionCardProps {
  step: TourStep;
  index: number;
  total: number;
  phaseLabel: string;
  actionHintText: string;
  nextText: string;
  prevText: string;
  skipAllText: string;
  canProceed: boolean;
  onNext: () => void;
  onPrev: () => void;
  onClose: () => void;
  onSkipAll: () => void;
}

const ActionCard: React.FC<ActionCardProps> = ({
  step, index, total, phaseLabel, actionHintText,
  nextText, prevText, skipAllText, canProceed, onNext, onPrev, onClose, onSkipAll,
}) => {
  const progress = Math.round(((index + 1) / total) * 100);

  return (
    <div className="tour-action-card">
      <button className="tour-action-card__close" onClick={onClose} aria-label="Close">×</button>
      <div className="tour-action-card__title">{step.title}</div>
      <div className="tour-popover-content">
        <div className="tour-phase-badge">{phaseLabel}</div>
        <p className="tour-description" dangerouslySetInnerHTML={{ __html: step.description }} />
        <div className="tour-action-hint">👆 {actionHintText}</div>
        <div className="tour-progress-bar">
          <div className="tour-progress-fill" style={{ width: `${progress}%` }} />
        </div>
        <div className="tour-progress-text">{index + 1} / {total}</div>
      </div>
      <div className="tour-action-card__footer">
        <button className="tour-action-card__btn tour-action-card__btn--skip" onClick={onSkipAll}>
          {skipAllText}
        </button>
        <div className="tour-action-card__nav">
          {index > 0 && (
            <button className="tour-action-card__btn tour-action-card__btn--prev" onClick={onPrev}>
              ← {prevText}
            </button>
          )}
          <button className="tour-action-card__btn tour-action-card__btn--next" onClick={onNext} disabled={!canProceed}>
            {nextText} →
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Provider ────────────────────────────────────────────────────────
export const WorkflowTourProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { t } = useTranslation('workflow');
  const navigate = useNavigate();
  const location = useLocation();

  const [isActive, setIsActive] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [hasSeenTour] = useState(() => {
    return localStorage.getItem(STORAGE_KEY_SEEN) === 'true';
  });

  const driverRef = useRef<Driver | null>(null);
  const stepsRef = useRef<TourStep[]>([]);

  // Refs to break circular dependency between buildDriverStep ↔ goToStep
  const goToStepRef = useRef<(idx: number) => void>(() => {});
  const stopTourRef = useRef<() => void>(() => {});

  // ── Build a driver.js step (only for guide/info steps with element) ──
  const buildDriverStep = useCallback(
    (step: TourStep, index: number, totalSteps: number): DriveStep => {
      const progress = Math.round(((index + 1) / totalSteps) * 100);
      const phaseLabelMap: Record<number, string> = {
        1: t('tour_phase1'),
        2: t('tour_phase2'),
        3: t('tour_phase3'),
        4: t('tour_phase4'),
      };
      const phaseLabel = phaseLabelMap[step.phase] || '';
      const isLast = index === totalSteps - 1;

      const descriptionHtml = `
        <div class="tour-popover-content">
          <div class="tour-phase-badge">${phaseLabel}</div>
          <p class="tour-description">${step.description}</p>
          <div class="tour-progress-bar">
            <div class="tour-progress-fill" style="width: ${progress}%"></div>
          </div>
          <div class="tour-progress-text">${index + 1} / ${totalSteps}</div>
          <button class="tour-skip-all-btn" onclick="document.dispatchEvent(new CustomEvent('tour-skip-all'))">${t('tour_skipAll')}</button>
        </div>
      `;

      return {
        element: step.element!,
        popover: {
          title: step.title,
          description: descriptionHtml,
          side: (step.side || 'bottom') as any,
          align: (step.align || 'center') as any,
          showButtons: isLast
            ? (['close'] as any)
            : index === 0
              ? (['next', 'close'] as any)
              : (['previous', 'next', 'close'] as any),
          nextBtnText: isLast ? `🏆 ${t('tour_finish')}` : t('tour_next'),
          prevBtnText: t('tour_prev'),
          doneBtnText: t('tour_finish'),
          onNextClick: () => goToStepRef.current(index + 1),
          onPrevClick: () => goToStepRef.current(index - 1),
          onCloseClick: () => stopTourRef.current(),
        },
      };
    },
    [t],
  );

  // ── Stop tour ─────────────────────────────────────────────────────
  const stopTour = useCallback(() => {
    driverRef.current?.destroy();
    driverRef.current = null;
    setIsActive(false);
  }, []);
  stopTourRef.current = stopTour;

  // ── Save/Load progress ────────────────────────────────────────────
  const saveProgress = (stepIndex: number) => {
    const data: TourProgress = { stepIndex, completed: false };
    localStorage.setItem(STORAGE_KEY_PROGRESS, JSON.stringify(data));
  };

  const loadProgress = (): TourProgress | null => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY_PROGRESS);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  };

  // ── Navigate to step ──────────────────────────────────────────────
  const goToStep = useCallback(
    (targetIndex: number) => {
      const steps = stepsRef.current;
      if (targetIndex < 0 || targetIndex >= steps.length) {
        stopTour();
        localStorage.setItem(STORAGE_KEY_SEEN, 'true');
        localStorage.setItem(
          STORAGE_KEY_PROGRESS,
          JSON.stringify({ stepIndex: steps.length - 1, completed: true }),
        );
        return;
      }

      const step = steps[targetIndex];

      // Handle navigateTo: navigate first, then show step after delay
      if (step.navigateTo) {
        navigate(step.navigateTo);
        driverRef.current?.destroy();
        driverRef.current = null;
        setTimeout(() => {
          setCurrentStep(targetIndex);
          saveProgress(targetIndex);
        }, 500);
        return;
      }

      // Action step → destroy driver.js, show React card
      if (step.isAction) {
        driverRef.current?.destroy();
        driverRef.current = null;
        setCurrentStep(targetIndex);
        saveProgress(targetIndex);
        return;
      }

      // Guide step with element → use driver.js
      driverRef.current?.destroy();

      const driverStep = buildDriverStep(step, targetIndex, steps.length);
      const d = driver({
        showProgress: false,
        animate: true,
        allowClose: true,
        overlayColor: 'rgba(0, 0, 0, 0.5)',
        stagePadding: 10,
        stageRadius: 12,
        popoverClass: 'tour-popover',
        steps: [driverStep],
        onDestroyed: () => {
          // Handled by goToStep / stopTour
        },
      });

      driverRef.current = d;
      setCurrentStep(targetIndex);
      saveProgress(targetIndex);

      setTimeout(() => {
        d.drive(0);
      }, 100);
    },
    [buildDriverStep, navigate, stopTour],
  );
  goToStepRef.current = goToStep;

  // ── Start tour ────────────────────────────────────────────────────
  const startTour = useCallback(
    (fromBeginning = true) => {
      let startAt = 0;
      if (!fromBeginning) {
        const saved = loadProgress();
        if (saved && !saved.completed) {
          startAt = saved.stepIndex;
        }
      }

      const steps = getTourSteps();
      stepsRef.current = steps;

      // Make sure we're on the right page
      const step = steps[startAt];
      if (step?.phase <= 3 && location.pathname !== '/workflows' && !location.pathname.startsWith('/workflows/')) {
        navigate('/workflows');
        setTimeout(() => {
          setIsActive(true);
          goToStep(startAt);
        }, 500);
      } else if (step?.phase === 4 && location.pathname !== '/schedules') {
        navigate('/schedules');
        setTimeout(() => {
          setIsActive(true);
          goToStep(startAt);
        }, 500);
      } else {
        setIsActive(true);
        goToStep(startAt);
      }
    },
    [goToStep, navigate, location.pathname],
  );

  // ── Cleanup on unmount ────────────────────────────────────────────
  useEffect(() => {
    const handleSkipAll = () => {
      stopTour();
      localStorage.setItem(STORAGE_KEY_SEEN, 'true');
      localStorage.setItem(
        STORAGE_KEY_PROGRESS,
        JSON.stringify({ stepIndex: stepsRef.current.length - 1, completed: true }),
      );
    };
    document.addEventListener('tour-skip-all', handleSkipAll);
    return () => {
      document.removeEventListener('tour-skip-all', handleSkipAll);
      driverRef.current?.destroy();
    };
  }, [stopTour]);

  // ── Highlight + overlay for action steps ────────────────────────
  // Applies a CSS class to the target element and tracks its bounding
  // rect so we can render an SVG overlay with a transparent cutout.
  const highlightRef = useRef<Element | null>(null);
  const secondaryHighlightRef = useRef<Element | null>(null);
  const [spotlightRect, setSpotlightRect] = useState<{ x: number; y: number; w: number; h: number } | null>(null);
  const [secondarySpotlightRect, setSecondarySpotlightRect] = useState<{ x: number; y: number; w: number; h: number } | null>(null);

  useEffect(() => {
    // Remove previous highlight
    if (highlightRef.current) {
      highlightRef.current.classList.remove('tour-highlight-ring');
      highlightRef.current = null;
    }
    if (secondaryHighlightRef.current) {
      secondaryHighlightRef.current.classList.remove('tour-highlight-ring');
      secondaryHighlightRef.current = null;
    }
    setSpotlightRect(null);
    setSecondarySpotlightRect(null);

    const steps = stepsRef.current;
    const stepData = steps[currentStep];

    if (!isActive || !stepData?.isAction) return;

    // If no highlightElement, just show plain overlay (no cutout)
    if (!stepData.highlightElement) {
      setSpotlightRect(null);
      setSecondarySpotlightRect(null);
      return;
    }

    // Small delay to let navigation / DOM updates settle
    const timer = setTimeout(() => {
      const el = document.querySelector(stepData.highlightElement!);
      if (el) {
        el.classList.add('tour-highlight-ring');
        highlightRef.current = el;
        el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });

        const rect = el.getBoundingClientRect();
        setSpotlightRect({ x: rect.x, y: rect.y, w: rect.width, h: rect.height });
      }

      // Secondary highlight (e.g. canvas area during drag steps)
      if (stepData.secondaryHighlightElement) {
        const el2 = document.querySelector(stepData.secondaryHighlightElement);
        if (el2) {
          el2.classList.add('tour-highlight-ring');
          secondaryHighlightRef.current = el2;

          const rect2 = el2.getBoundingClientRect();
          setSecondarySpotlightRect({ x: rect2.x, y: rect2.y, w: rect2.width, h: rect2.height });
        }
      }
    }, 200);

    // Update cutout position on scroll / resize
    const updateRect = () => {
      if (highlightRef.current) {
        const rect = highlightRef.current.getBoundingClientRect();
        setSpotlightRect({ x: rect.x, y: rect.y, w: rect.width, h: rect.height });
      }
      if (secondaryHighlightRef.current) {
        const rect2 = secondaryHighlightRef.current.getBoundingClientRect();
        setSecondarySpotlightRect({ x: rect2.x, y: rect2.y, w: rect2.width, h: rect2.height });
      }
    };
    window.addEventListener('scroll', updateRect, true);
    window.addEventListener('resize', updateRect);

    return () => {
      clearTimeout(timer);
      window.removeEventListener('scroll', updateRect, true);
      window.removeEventListener('resize', updateRect);
      if (highlightRef.current) {
        highlightRef.current.classList.remove('tour-highlight-ring');
        highlightRef.current = null;
      }
      if (secondaryHighlightRef.current) {
        secondaryHighlightRef.current.classList.remove('tour-highlight-ring');
        secondaryHighlightRef.current = null;
      }
    };
  }, [isActive, currentStep]);

  // ── Get current step data for action card ─────────────────────────
  const steps = stepsRef.current;
  const currentStepData = steps[currentStep];
  const isActionStep = isActive && currentStepData?.isAction;

  // ── Validate action step completion ───────────────────────────────
  // Poll the validate function every 500ms to enable/disable the Next button.
  const [canProceed, setCanProceed] = useState(false);

  useEffect(() => {
    if (!isActive || !currentStepData?.isAction) {
      setCanProceed(true);
      return;
    }

    // No validate fn → always allow proceeding
    if (!currentStepData.validate) {
      setCanProceed(true);
      return;
    }

    // Check immediately
    setCanProceed(currentStepData.validate());

    // Then poll
    const interval = setInterval(() => {
      setCanProceed(currentStepData.validate!());
    }, 500);

    return () => clearInterval(interval);
  }, [isActive, currentStep, currentStepData]);

  const phaseLabelMap: Record<number, string> = {
    1: t('tour_phase1'),
    2: t('tour_phase2'),
    3: t('tour_phase3'),
    4: t('tour_phase4'),
  };

  return (
    <TourContext.Provider
      value={{
        startTour,
        isActive,
        currentStep,
        totalSteps: TOUR_TOTAL_STEPS,
        hasSeenTour,
      }}
    >
      {children}

      {/* SVG overlay with transparent cutout for action steps */}
      {isActionStep && (
        <div className="tour-overlay">
          <svg width="100%" height="100%">
            {spotlightRect ? (
              <>
                <defs>
                  <mask id="tour-spotlight-mask">
                    <rect width="100%" height="100%" fill="white" />
                    <rect
                      x={spotlightRect.x - 8}
                      y={spotlightRect.y - 8}
                      width={spotlightRect.w + 16}
                      height={spotlightRect.h + 16}
                      rx="12"
                      fill="black"
                    />
                    {secondarySpotlightRect && (
                      <rect
                        x={secondarySpotlightRect.x - 8}
                        y={secondarySpotlightRect.y - 8}
                        width={secondarySpotlightRect.w + 16}
                        height={secondarySpotlightRect.h + 16}
                        rx="12"
                        fill="black"
                      />
                    )}
                  </mask>
                </defs>
                <rect
                  width="100%"
                  height="100%"
                  fill="rgba(0, 0, 0, 0.5)"
                  mask="url(#tour-spotlight-mask)"
                />
              </>
            ) : (
              <rect width="100%" height="100%" fill="rgba(0, 0, 0, 0.5)" />
            )}
          </svg>
        </div>
      )}

      {/* Floating action card for isAction steps — rendered via React, not driver.js */}
      {isActionStep && currentStepData && (
        <ActionCard
          step={currentStepData}
          index={currentStep}
          total={steps.length}
          phaseLabel={phaseLabelMap[currentStepData.phase] || ''}
          actionHintText={t('tour_actionRequired')}
          nextText={currentStep === steps.length - 1 ? t('tour_finish') : t('tour_next')}
          prevText={t('tour_prev')}
          skipAllText={t('tour_skipAll')}
          canProceed={canProceed}
          onNext={() => goToStep(currentStep + 1)}
          onPrev={() => goToStep(currentStep - 1)}
          onClose={stopTour}
          onSkipAll={() => {
            stopTour();
            localStorage.setItem(STORAGE_KEY_SEEN, 'true');
            localStorage.setItem(
              STORAGE_KEY_PROGRESS,
              JSON.stringify({ stepIndex: steps.length - 1, completed: true }),
            );
          }}
        />
      )}
    </TourContext.Provider>
  );
};
